// Client mirror of
// supabase/functions/generate-progress-claim/retention-ceiling.ts
// (the app does not import across into supabase/functions; parity is enforced by
// the deno tests in both places).
//
// SEQ-235: the retention ceiling is a percentage of the retention base, set per
// project (claims_projects.retention_ceiling_percent, default 5). One formula
// for every surface. The base is chosen elsewhere (retention_ceiling_mode,
// retention_custom_amount). The per-claim rate is a different number.

export const DEFAULT_RETENTION_CEILING_PERCENT = 5;

// A usable percentage (greater than 0, at most 100), else the default 5.
export function normalizeRetentionCeilingPercent(value: unknown): number {
  if (value == null || (typeof value === "string" && value.trim() === "")) {
    return DEFAULT_RETENTION_CEILING_PERCENT;
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0 || n > 100) {
    return DEFAULT_RETENTION_CEILING_PERCENT;
  }
  return n;
}

// Ceiling in dollars: base x percent / 100, rounded to cents.
export function retentionCeilingAmount(retentionBase: number, percent: unknown): number {
  const base = Number(retentionBase);
  if (!Number.isFinite(base)) return 0;
  const raw = (base * normalizeRetentionCeilingPercent(percent)) / 100;
  return Math.round((raw + Number.EPSILON) * 100) / 100;
}

// "5", "7.5", "10": no trailing zeros, for labels and PDF wording.
export function formatRetentionCeilingPercent(percent: unknown): string {
  return String(Number(normalizeRetentionCeilingPercent(percent).toFixed(2)));
}

// Form input check for the "Retention ceiling %" field (client only). The
// database column is numeric(5,2) with a CHECK of greater than 0 and at most 100.
export type RetentionCeilingPercentInput =
  | { ok: true; value: number }
  | { ok: false; message: string };

export const RETENTION_CEILING_PERCENT_MESSAGE =
  "Retention ceiling % must be a number above 0 and no more than 100, with at most 2 decimal places.";

export function parseRetentionCeilingPercentInput(text: string): RetentionCeilingPercentInput {
  const trimmed = text.trim();
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(trimmed)) {
    return { ok: false, message: RETENTION_CEILING_PERCENT_MESSAGE };
  }
  const n = Number(trimmed);
  if (!(n > 0 && n <= 100)) return { ok: false, message: RETENTION_CEILING_PERCENT_MESSAGE };
  return { ok: true, value: n };
}
